import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../api/index';

interface Vendor {
  id: string;
  email: string;
  businessName?: string | null;
  businessType?: string | null;
  status: string;
}

interface VendorAuthContextType {
  vendor: Vendor | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
  }) => Promise<void>;
  logout: () => void;
}

const VendorAuthContext = createContext<VendorAuthContextType | undefined>(undefined);

export function VendorAuthProvider({ children }: { children: ReactNode }) {
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored vendor token
    const storedToken = localStorage.getItem('hmh-vendor-token');
    const storedVendor = localStorage.getItem('hmh-vendor');

    if (storedToken && storedVendor) {
      try {
        setToken(storedToken);
        setVendor(JSON.parse(storedVendor));
      } catch (error) {
        console.error('Error parsing stored vendor data:', error);
        localStorage.removeItem('hmh-vendor-token');
        localStorage.removeItem('hmh-vendor');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/vendors/login', { email, password });
      const { vendor: vendorData, token: vendorToken } = response.data;

      setVendor(vendorData);
      setToken(vendorToken);
      localStorage.setItem('hmh-vendor-token', vendorToken);
      localStorage.setItem('hmh-vendor', JSON.stringify(vendorData));

      // Set token in axios defaults for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${vendorToken}`;
    } catch (error: any) {
      // Preserve the original error with response data so login page can access emailNotVerified
      const loginError: any = new Error(error.response?.data?.error || 'Login failed');
      loginError.response = error.response;
      throw loginError;
    }
  };

  const register = async (data: {
    email: string;
    password: string;
  }) => {
    try {
      await api.post('/vendors/register', data);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = () => {
    setVendor(null);
    setToken(null);
    localStorage.removeItem('hmh-vendor-token');
    localStorage.removeItem('hmh-vendor');
    delete api.defaults.headers.common['Authorization'];
    navigate('/vendor/login');
  };

  // Set authorization header when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  return (
    <VendorAuthContext.Provider value={{ vendor, token, loading, login, register, logout }}>
      {children}
    </VendorAuthContext.Provider>
  );
}

export function useVendorAuth() {
  const context = useContext(VendorAuthContext);
  if (context === undefined) {
    throw new Error('useVendorAuth must be used within a VendorAuthProvider');
  }
  return context;
}

export function getVendorToken(): string | null {
  return localStorage.getItem('hmh-vendor-token');
}

